/*
 * Descope validates phone numbers as E.164, while Member Hub stores Australian mobiles in
 * local format ("0412345678"). A stored number therefore cannot be handed to Descope as-is.
 *
 * Lives in shared/ because both sides need it: the browser converts before requesting an
 * SMS, and the Nitro route converts before calling the Management API.
 */

/** Dialling code applied to numbers sent to Descope. */
export const AU_COUNTRY_CODE = '+61'

/** Digits in an AU mobile after its trunk "0" and any country code are removed, e.g. "412345678". */
const AU_SIGNIFICANT_DIGITS = 9

export function toE164 (value?: string | null, countryCode: string = AU_COUNTRY_CODE): string {
  const raw = String(value || '').trim()
  if (!raw) return ''

  // Already carries a dialling code — strip formatting and leave it alone.
  if (raw.startsWith('+')) return raw.replace(/[^\d+]/g, '')

  const digits = raw.replace(/\D/g, '')
  if (!digits) return ''

  const bareCode = countryCode.replace(/\D/g, '')
  // "61412345678" — the code is present, just missing its plus.
  if (bareCode && digits.startsWith(bareCode) && digits.length === bareCode.length + AU_SIGNIFICANT_DIGITS) {
    return `+${digits}`
  }
  // "0412345678" — local trunk prefix, which E.164 drops.
  if (digits.startsWith('0') && digits.length === AU_SIGNIFICANT_DIGITS + 1) {
    return `${countryCode}${digits.slice(1)}`
  }
  // "412345678" — bare AU number missing its trunk zero.
  if (digits.length === AU_SIGNIFICANT_DIGITS) return `${countryCode}${digits}`

  /*
   * Doesn't match any plausible AU shape (wrong digit count for all three cases above) —
   * most likely a non-AU number that already carried its own dialling code before
   * something upstream (e.g. withoutCountryCode, which only knows how to strip AU's own
   * code) dropped the leading "+". Restoring just the "+" avoids compounding that into a
   * second, wrong country code stacked on top of the real one — which is what silently
   * corrupted a non-AU test account's number into "+61" + its own already-present code.
   */
  return `+${digits}`
}

/*
 * E.164 assigned country calling codes, longest first so "+972" is not read as "+97".
 * Stored as a string to keep the source compact; parsed once.
 *
 * A table is unavoidable: codes are 1-3 digits and ambiguous by prefix, so "+918129233957"
 * cannot be split without knowing that 91 is assigned and 918 is not. If fuller parsing is
 * ever needed (validation, national formatting), a library like libphonenumber-js would
 * replace this — that is a dependency call, so it is not made here.
 */
const COUNTRY_CALLING_CODES = new Set(`
1 7
20 27 30 31 32 33 34 36 39 40 41 43 44 45 46 47 48 49 51 52 53 54 55 56 57 58
60 61 62 63 64 65 66 81 82 84 86 90 91 92 93 94 95 98
211 212 213 216 218 220 221 222 223 224 225 226 227 228 229 230 231 232 233 234
235 236 237 238 239 240 241 242 243 244 245 246 247 248 249 250 251 252 253 254
255 256 257 258 260 261 262 263 264 265 266 267 268 269 290 291 297 298 299
350 351 352 353 354 355 356 357 358 359 370 371 372 373 374 375 376 377 378 379
380 381 382 383 385 386 387 389 420 421 423 500 501 502 503 504 505 506 507 508
509 590 591 592 593 594 595 596 597 598 599 670 672 673 674 675 676 677 678 679
680 681 682 683 685 686 687 688 689 690 691 692 800 808 850 852 853 855 856 870
878 880 881 882 883 886 888 960 961 962 963 964 965 966 967 968 970 971 972 973
974 975 976 977 992 993 994 995 996 998
`.trim().split(/\s+/))

/*
 * What gets written back to Member Hub, which holds numbers without a dialling code.
 *
 * Strips a country code and nothing else — no digit is ever added. A number already on the
 * profile round-trips unchanged, and a typed number is stored as typed minus its code. Only
 * the SMS request gets the E.164 form.
 *
 * The code is only stripped when the value was written in international form (a leading "+",
 * or the configured code with no trunk "0"). A plain local number is left alone, so
 * "0412345678" does not lose its "04" to the "41" entry in the table.
 */
export function withoutCountryCode (value?: string | null, countryCode: string = AU_COUNTRY_CODE): string {
  const raw = String(value || '').trim()
  const digits = raw.replace(/\D/g, '')
  if (!digits) return ''

  const bareCode = countryCode.replace(/\D/g, '')

  // Written internationally: an explicit "+" means every leading digit is the country code.
  if (raw.startsWith('+')) {
    for (const length of [3, 2, 1]) {
      const prefix = digits.slice(0, length)
      if (COUNTRY_CALLING_CODES.has(prefix)) return digits.slice(length)
    }
    return digits
  }

  // No "+", but the configured code with no trunk prefix — e.g. "61412345678".
  if (bareCode && !digits.startsWith('0') && digits.startsWith(bareCode)) {
    return digits.slice(bareCode.length)
  }

  return digits
}

/*
 * Member Hub masks phone_number on profiles it considers unverified — "**********", or
 * partially as "*******6560". A masked value is not a real, SMS-able number and must never
 * be prefilled into the enrolment field.
 */
export function isMaskedPhone (value?: string | null): boolean {
  return String(value || '').includes('*')
}

/*
 * Whether a stored number can be prefilled: present, unmasked, and carrying enough digits
 * to be a phone number.
 *
 * Deliberately looser than phoneNumberValidator, which only accepts local AU format
 * ("0412345678"). That rejected perfectly usable values — an E.164 number the user typed
 * themselves ("+61412345678") or a spaced one ("0412 345 678") — so the field came up empty
 * and the number had to be retyped.
 */
export function isUsablePhone (value?: string | null): boolean {
  if (!value || isMaskedPhone(value)) return false

  return String(value).replace(/\D/g, '').length >= 8
}

/*
 * --- Split phone fields (country picker + number box) ---
 *
 * The functions below back V2MoleculesPhoneInput. They take the supported-country list as
 * an argument (PHONE_COUNTRIES in app/constants/country.constants.js) rather than importing
 * it, because this file is shared with the server and cannot reach app/ constants.
 */

export interface PhoneCountry {
  iso: string
  name: string
  phoneCode: string
  /** Exact national length, when known; null for countries checked generically. */
  nationalLength: number | null
  /** Most national digits the number box accepts. */
  maxLength?: number
  mobilePattern: RegExp
  anyPattern: RegExp
  groups: number[]
  localTrunk: boolean
  /** "Australian" — used in error messages; falls back to `name`. */
  demonym?: string
  /** The country a shared dialling code is read as by default (+44 → United Kingdom). */
  primaryForCode?: boolean
}

export interface PhoneParts {
  iso: string
  /** Digits after the dialling code, with no trunk "0" — e.g. "412345678". */
  national: string
  /** Set when the value carried a dialling code none of the supported countries use. */
  unsupported?: boolean
}

export type PhoneFormat = 'e164' | 'local' | 'national'

const TRUNK_PREFIX = '0'

function findCountry (countries: PhoneCountry[], iso?: string): PhoneCountry | undefined {
  return countries.find(country => country.iso === iso)
}

function codeDigits (country: PhoneCountry): string {
  return country.phoneCode.replace(/\D/g, '')
}

/*
 * Splits any stored or typed number into country + national digits.
 *
 * `preferIso` is a country the user explicitly picked. It changes one rule: a leading "0"
 * normally means Australia (its trunk prefix), but once someone has chosen India or the
 * Philippines, their own "0917…" is that country's trunk prefix, not a switch to AU.
 *
 * A bare number with no code and no trunk "0" cannot be placed by its digits alone —
 * Member Hub stores numbers that way, code stripped — so it takes the preferred or
 * default country.
 */
export function parsePhone (
  value: string | null | undefined,
  { countries, defaultIso, preferIso }: { countries: PhoneCountry[], defaultIso: string, preferIso?: string }
): PhoneParts {
  const fallbackIso = preferIso || defaultIso
  const raw = String(value || '').trim()
  if (!raw || isMaskedPhone(raw)) return { iso: fallbackIso, national: '' }

  const digits = raw.replace(/\D/g, '')
  if (!digits) return { iso: fallbackIso, national: '' }

  // Longest code first, so a 3-digit code is never read as a 2-digit one. The sort is
  // stable, so countries sharing a code keep the list's order (Australia before the Cocos
  // Islands for +61).
  const byCodeLength = [...countries].sort((a, b) => codeDigits(b).length - codeDigits(a).length)

  if (raw.startsWith('+')) {
    const longest = byCodeLength.find(country => digits.startsWith(codeDigits(country)))
    if (!longest) return { iso: fallbackIso, national: digits, unsupported: true }

    // Several countries can share a code (+1 US/Canada, +44 UK/Jersey…). Keep the one the
    // user picked if it is among them; then the code's primary country; then list order.
    const code = codeDigits(longest)
    const sharing = countries.filter(country => codeDigits(country) === code)
    const match = sharing.find(country => country.iso === preferIso)
      || sharing.find(country => country.primaryForCode)
      || longest

    // "+61 0412 345 678" — a trunk 0 kept after the code is dropped, as E.164 requires.
    const rest = digits.slice(codeDigits(match).length)
    return { iso: match.iso, national: rest.startsWith(TRUNK_PREFIX) ? rest.slice(1) : rest }
  }

  if (digits.startsWith(TRUNK_PREFIX)) {
    const preferred = findCountry(countries, preferIso)
    const iso = preferred && preferred.iso !== 'AU' ? preferred.iso : 'AU'
    return { iso, national: digits.slice(1) }
  }

  // Code written without its "+", e.g. "61412345678" — only for countries with an exact
  // length, and only when it fits exactly; anything looser would misread national numbers.
  const withCode = byCodeLength.find(country =>
    country.nationalLength
    && digits.startsWith(codeDigits(country))
    && digits.length === codeDigits(country).length + country.nationalLength)
  if (withCode) return { iso: withCode.iso, national: digits.slice(codeDigits(withCode).length) }

  return { iso: fallbackIso, national: digits }
}

/*
 * e164:     "+61412345678"
 * local:    "0412345678" for AU (Member Hub's and phoneNumberValidator's format); for other
 *           countries the national digits, since their trunk prefix is not stored.
 * national: "412345678"
 *
 * Empty national digits always format to "", so a required check on the result works.
 */
export function formatPhone (parts: PhoneParts, format: PhoneFormat, countries: PhoneCountry[]): string {
  const country = findCountry(countries, parts.iso)
  const national = String(parts.national || '').replace(/\D/g, '')
  if (!national || !country) return ''

  if (format === 'e164') return `${country.phoneCode}${national}`
  if (format === 'local') return country.localTrunk ? `${TRUNK_PREFIX}${national}` : national
  return national
}

export function isValidNational (parts: PhoneParts, countries: PhoneCountry[], kind: 'mobile' | 'any' = 'mobile'): boolean {
  const country = findCountry(countries, parts.iso)
  if (!country || parts.unsupported) return false

  return (kind === 'any' ? country.anyPattern : country.mobilePattern).test(parts.national)
}

/*
 * The one place the split phone field's error wording lives, so the field (on blur) and
 * the form that owns it (on submit) can never say different things. "" means valid.
 */
export function describePhoneError (parts: PhoneParts, countries: PhoneCountry[], kind: 'mobile' | 'any' = 'mobile'): string {
  const noun = kind === 'mobile' ? 'mobile number' : 'phone number'

  if (parts.unsupported) {
    // A short, restricted list can be named; the full list cannot usefully be.
    if (countries.length > 4) return 'Check the country code — we don\'t recognise it'
    const names = countries.map(country => country.demonym || country.name)
    const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}` : names[0]
    return `We can only use ${list} numbers`
  }
  if (!parts.national) return `Enter your ${noun}`
  if (!isValidNational(parts, countries, kind)) {
    const country = findCountry(countries, parts.iso)
    return `Enter a valid ${country?.demonym || country?.name || ''} ${noun}`.replace(/\s+/g, ' ')
  }
  return ''
}

/*
 * Spaces digits for the number box, e.g. AU "412345678" -> "412 345 678". A typed trunk
 * "0" is kept and joins the first group ("0412 345 678"), so the box shows what was typed.
 * Digits past the country's length are left in an unspaced tail rather than dropped, so
 * nothing the user typed silently disappears; validation reports the excess.
 */
export function formatNationalDisplay (typedDigits: string, country: PhoneCountry): string {
  const digits = String(typedDigits || '').replace(/\D/g, '')
  if (!digits) return ''
  // Countries without known grouping are shown unspaced rather than spaced wrongly.
  if (!country.groups?.length) return digits

  const hasTrunk = digits.startsWith(TRUNK_PREFIX)
  const groups = [...country.groups]
  if (hasTrunk && groups.length) groups[0] += 1

  const parts: string[] = []
  let index = 0
  for (const size of groups) {
    if (index >= digits.length) break
    parts.push(digits.slice(index, index + size))
    index += size
  }
  const tail = digits.slice(index)

  return tail ? `${parts.join(' ')} ${tail}` : parts.join(' ')
}
